import React, { useRef, useState, useEffect } from 'react';
import {
  Button,
  TextField,
  MenuItem,
  Menu,
  IconButton,
  Typography,
  Tooltip,
  Grid,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import InvalidIcon from '@material-ui/icons/Warning';
import { css } from '@emotion/core';
import { VoucherTransactionSchema } from '../../../validation';
import { ButtonFlex } from '../../styles/styles';
import { NumberHandler } from '../../util/NumberHandler';
import { safeEval, formatPrice } from '../../util/UtilFunctions';
import { useRootStore } from '../../../stores/BridgeProvider';
import Spinner from '../../util/Spinner';
import ACDropdown from '../../util/ACDropdown';
import { COLORS } from '../../util/Colors';
import AccountSelectAC from '../../util/AccountSelectAC';

const vtHeaders = css`
  .field-header {
    border-right: 1px solid rgba(0, 0, 0, 0.05);
    font-weight: 700;
    height: 60px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const TableRow = css`
  .number-div {
    display: flex;
    height: 100%;
    align-items: center;
    box-sizing: border-box;
    border: 1px solid lightgrey;
    border-radius: 4px;
    padding: 0 4px;
  }
  .cell-item {
  }
  input {
    font-family: system-ui;
    font-size: 16px;
    color: #666666;
  }
`;

const ValidationErrors = ({ transaction }) => {
  const { errors } = transaction;
  if (!errors) return null;

  return (
    <Tooltip
      placement="right"
      title={
        <ul style={{ padding: 0, listStyleType: 'none' }}>
          {errors.map(error => (
            <li key={error}>* {error}</li>
          ))}
        </ul>
      }
    >
      <InvalidIcon
        style={{
          color: COLORS.red,
        }}
      />
    </Tooltip>
  );
};

const VoucherTranFormRow = ({
  transaction,
  trancatOptions,
  addRow,
  deleteVoucherTransaction,
  index,
  VoucherType,
  VoucherTypeId,
}) => {
  const { dataStore, authStore, orgStore } = useRootStore();
  const { settings } = orgStore;
  const {
    VoucherTypeDefault,
    ARAccountId: defaultAR,
    APAccountId: defaultAP,
    ARBankAccountId: defaultARBank,
    APBankAccountId: defaultAPBank,
  } = settings;
  const transRef = useRef(transaction);
  const { Price, Quantity, CategoryId, AccountId, Description } = transaction;
  const [curPrice, setCurPrice] = useState(formatPrice(Price));
  const [curCount, setCurCount] = useState(Quantity);
  const [curTranCategory, setCurTranCategory] = useState(CategoryId);
  const [curAccount, setCurAccount] = useState(AccountId);

  const [curDesc, setCurDesc] = useState(Description);
  const Account = dataStore.getAccountFromId(curAccount);

  const [anchorEl, setAnchorEl] = useState(null);

  const isValidAccountGroup = TypeId => {
    if ([1, 3].includes(VoucherTypeId)) {
      return [6, 3, 4, 5, 7].includes(TypeId);
    }
    if ([2, 4].includes(VoucherTypeId)) {
      return [7, 3, 4, 5, 6].includes(TypeId);
    }
    return true;
  };

  const filteredTranCategories = () => {
    const curId = transRef.current.CategoryId;

    return trancatOptions.filter(
      ({
        APAccountId,
        APContraAccountId,
        ARAccountId,
        ARContraAccountId,
        TranCategoryId,
      }) => {
        // If the category is already set then it should exist in the dropdown
        if (curId && curId === TranCategoryId) {
          return true;
        }
        if (!VoucherType) return true;
        if ([1, 3, 5].includes(VoucherTypeId)) {
          return ARAccountId && ARContraAccountId;
        }
        if ([2, 4, 6].includes(VoucherTypeId)) {
          return APAccountId && APContraAccountId;
        }

        return false;
      },
    );
  };

  const filteredOptions = filteredTranCategories();

  const open = Boolean(anchorEl);

  const handleChange = (field, newVal) => {
    if (transRef.current[field] !== newVal) {
      transRef.current.dirty = true;
    }
    switch (field) {
      case 'CategoryId':
        transRef.current[field] = newVal;
        try {
          const {
            APContraAccountId,
            ARContraAccountId,
            Name,
            Code,
          } = dataStore.getTranCategoryFromId(newVal);

          if (VoucherType === 'Receivable') {
            setCurAccount(ARContraAccountId);
            transRef.current.AccountId = ARContraAccountId;
          }
          if (VoucherType === 'Payable') {
            setCurAccount(APContraAccountId);
            transRef.current.AccountId = APContraAccountId;
          }
          setCurDesc(Name || Code);
          transRef.current.Description = Name || Code;
        } catch (e) {
          console.error(e);
        }
        break;
      case 'Price':
      case 'Quantity':
      case 'AccountId':
      default:
        transRef.current[field] = newVal;
    }
  };

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const moveAccountTypeToFront = (list, typeId) => {
    const sorter = (a, b, arr) => {
      if (arr.includes(a.TypeId)) {
        return -1;
      }
      if (arr.includes(b.TypeId)) {
        return 1;
      }
      return a.TypeId - b.TypeId;
    };

    return list.sort((a, b) => sorter(a, b, typeId));
  };

  return (
    <Grid container wrap="nowrap" item xs={12} spacing={1} css={TableRow}>
      <Grid item xs={2}>
        <div className="cell-item">
          <ACDropdown
            options={filteredOptions}
            onChange={(e, newVal) => {
              newVal !== null &&
                (setCurTranCategory(newVal.TranCategoryId),
                handleChange('CategoryId', newVal.TranCategoryId));
            }}
            value={
              filteredOptions.find(x => x.TranCategoryId === curTranCategory) ||
              null
            }
            getOptionLabel={value => value.Code || null}
            getOptionSelected={(option, value) => {
              if (value === '') {
                return true;
              }
              if (option.TranCategoryId === value.TranCategoryId) {
                return true;
              }
            }}
          />
        </div>
      </Grid>
      <Grid item xs={3}>
        <div className="cell-item">
          <AccountSelectAC
            value={Account || null}
            isValidAccountGroup={isValidAccountGroup}
            sortFunction={list => {
              if ([1, 3].includes(VoucherTypeId)) {
                return moveAccountTypeToFront(list, [6]);
              }
              if ([2, 4].includes(VoucherTypeId)) {
                return moveAccountTypeToFront(list, [7]);
              }
              return list;
            }}
            onChange={(e, val) => {
              setCurAccount(val.AccountId);
              handleChange(
                'AccountId',
                safeEval(() => val.AccountId, null),
              );
            }}
            getOptionSelected={(option, value) => {
              if (value === '') {
                return true;
              }
              if (option.AccountId === value.AccountId) {
                return true;
              }
            }}
          />
        </div>
      </Grid>
      <Grid item xs={2}>
        <div className="cell-item">
          <TextField
            style={{ width: '100%' }}
            variant="outlined"
            value={curPrice}
            onChange={e => setCurPrice(e.target.value)}
            onBlur={handleChange('Price', curPrice)}
          />
        </div>
      </Grid>
      <Grid item xs={1}>
        <div className="cell-item">
          <TextField
            style={{ width: '100%' }}
            variant="outlined"
            value={Math.round(curCount)}
            onChange={e => setCurCount(e.target.value)}
            onBlur={handleChange('Quantity', curCount)}
            type="number"
            step="1"
          />
        </div>
      </Grid>
      <Grid item style={{ minWidth: '9%' }}>
        <div className="number-div">
          <NumberHandler
            expo={{ limit: 7 }}
            style={{ height: 'fit-content' }}
            num={transRef.current.Price * transRef.current.Quantity}
            currency="$"
            justify="flex-end"
            float={2}
          />
        </div>
      </Grid>
      <Grid item xs={2}>
        <div className="cell-item">
          <TextField
            style={{ width: '100%' }}
            variant="outlined"
            name="Description"
            type="text"
            value={curDesc}
            onChange={e => setCurDesc(e.target.value)}
            onBlur={() => handleChange('Description', curDesc)}
          />
        </div>
      </Grid>
      <Grid item xs={1}>
        <div
          className="cell-item"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <IconButton onClick={e => handleClick(e)}>
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <MenuItem
              onClick={() => deleteVoucherTransaction(transRef.current, index)}
            >
              Delete Transaction
            </MenuItem>
            <MenuItem onClick={() => addRow(transRef.current)}>
              Clone Transaction
            </MenuItem>
          </Menu>
          <ValidationErrors transaction={transaction} />
        </div>
      </Grid>
    </Grid>
  );
};

// **************** FORM ROW COMPONENT END

// **************** FORM COMPONENT START

const VoucherTranTableForm = ({
  initialVal,
  VoucherId,
  wipeChanges,
  setVoucherTransactions,
  VoucherType,
  VoucherTypeId,
}) => {
  const { authStore, dataStore } = useRootStore();
  const [transactions, setTransactions] = useState(
    initialVal || [
      {
        AccountCode: '',
        AccountId: '',
        AccountName: '',
        CategoryId: '',
        Price: '',
        Quantity: 1,
        Description: '',
        TranCategoryCode: '',
        TranCategoryName: '',
        VoucherId: '',
        VoucherTranId: '',
        dirty: true,
      },
    ],
  );
  const [loading, setLoading] = useState(true);
  const transactionCategories = dataStore.validTransactionCategoryArray;
  const contacts = dataStore.contactArray;
  const accounts = dataStore.validAccountArray;

  const addRow = (clone = {}) => {
    const vtsClone = [...transactions];
    vtsClone.push({
      VoucherId,
      CategoryId: clone.CategoryId || '',
      AccountId: clone.AccountId || '',
      Price: clone.Price || '',
      Quantity: clone.Quantity || 1,
      Description: clone.Description || '',
      dirty: true,
    });
    setTransactions(vtsClone);
  };

  const saveVoucherTransaction = async voucherTransactionToUpdate => {
    try {
      await authStore.request.post(
        `${process.env.REACT_APP_API}/api/voucher-transaction`,
        {
          VoucherTransactionId: voucherTransactionToUpdate.VoucherTranId,
          VoucherId: voucherTransactionToUpdate.VoucherId,
          CategoryId: voucherTransactionToUpdate.CategoryId,
          AccountId: voucherTransactionToUpdate.AccountId,
          Price: voucherTransactionToUpdate.Price,
          Quantity: voucherTransactionToUpdate.Quantity,
          Description: voucherTransactionToUpdate.Description,
        },
      );
    } catch (e) {
      console.error(e);
    }
  };

  const deleteVoucherTransaction = async (tran, index) => {
    const { VoucherTranId } = tran;
    if (VoucherTranId) {
      try {
        await authStore.request.delete(
          `${process.env.REACT_APP_API}/api/voucher-transaction`,
          {
            data: { VoucherTransactionId: VoucherTranId },
          },
        );
      } catch (e) {
        console.error(e);
      }
    }
    const vtsClone = [...transactions];
    vtsClone.splice(index, 1);
    setTransactions(vtsClone);
  };

  const validateRequests = async () => {
    const vtsClone = [...transactions];

    const queue = [];
    const errorQ = [];

    await Promise.all(
      vtsClone.map(async t => {
        if (t.dirty) {
          try {
            const res = await VoucherTransactionSchema.validate(t, {
              abortEarly: false,
            });
            t.errors = null;
            queue.push(res);
            return res;
          } catch (res) {
            t.errors = res.errors;
            errorQ.push(res.errors);
            return res.errors;
          }
        }
      }),
    );
    return { queue, errorQ, vtsClone };
  };

  const handleSubmit = async () => {
    const requestQ = await validateRequests();
    const { queue, errorQ, vtsClone } = requestQ;

    if (errorQ.length > 0) {
      setTransactions(vtsClone);
    } else {
      await Promise.all(queue.map(t => saveVoucherTransaction(t)));
      setVoucherTransactions(vtsClone);
      wipeChanges();
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const requests = [];
      if (contacts.length === 0) {
        requests.push(dataStore.retrieveContacts());
      }
      if (transactionCategories.length === 0) {
        requests.push(dataStore.retrieveTransactionCategories());
      }
      if (accounts.length === 0) {
        requests.push(dataStore.retrieveAccounts());
      }
      await Promise.all(requests).then(() => {
        setLoading(false);
      });
    };
    initialize();
  }, []);

  if (loading) return <Spinner />;
  return (
    <div>
      <div
        className="header-row"
        style={{ display: 'flex', marginBottom: '10px' }}
      >
        <Typography variant="h4">Edit Transactions</Typography>
        <Button
          style={{ marginLeft: 'auto' }}
          variant="outlined"
          color="secondary"
          onClick={() => wipeChanges()}
        >
          Go Back
        </Button>
      </div>
      <Grid container spacing={2}>
        <Grid
          css={vtHeaders}
          container
          item
          xs={12}
          spacing={1}
          style={{
            margin: '10px 0',
            border: '1px solid rgba(0,0,0,0.05)',
            padding: '0',
            boxShadow: '0 1px 3px rgb(0 0 0 / 12%), 0 1px 2px rgb(0 0 0 / 10%)',
          }}
        >
          <Grid className="field-header" item xs={2}>
            Category
          </Grid>
          <Grid className="field-header" item xs={3}>
            Account
          </Grid>
          <Grid className="field-header" item xs={2}>
            Price
          </Grid>
          <Grid className="field-header" item xs={1}>
            Quantity
          </Grid>
          <Grid className="field-header" item xs={1}>
            Total
          </Grid>
          <Grid className="field-header" item xs={2}>
            Description
          </Grid>
          <Grid item xs={1}></Grid>
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            border: '1px solid rgba(0,0,0,0.05)',
            boxShadow: '0 1px 3px rgb(0 0 0 / 12%), 0 1px 2px rgb(0 0 0 / 10%)',
            maxHeight: '320px',
            overflowY: 'auto',
          }}
        >
          {transactions.map((t, index) => (
            <VoucherTranFormRow
              key={index}
              transaction={t}
              trancatOptions={transactionCategories}
              contacts={contacts}
              addRow={addRow}
              deleteVoucherTransaction={deleteVoucherTransaction}
              index={index}
              VoucherType={VoucherType}
              VoucherTypeId={VoucherTypeId}
            />
          ))}
        </Grid>
      </Grid>
      <ButtonFlex>
        <Button variant="outlined" color="primary" onClick={addRow}>
          Add New Transaction
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            handleSubmit();
          }}
        >
          Save Changes
        </Button>
      </ButtonFlex>
    </div>
  );
};

export default VoucherTranTableForm;
